import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Mapper } from '@core/services/mapper/base-mapper.mapper';
import { IResponseList } from '@shared-features/interfaces/response-list.interface';
import { ApiBaseService } from '@core/services/api-base.service';
import {
  IPagingPayload,
  ISortPayload,
} from '@shared-features/interfaces/common-api-payloads.interface';
import { filterNullEntity } from '@shared/utils/filter-null-entity.util';
import { ApiConstant } from '@shared/constants/api.constant';
import { Organization } from '../models/organization';
import { Customer } from '@features/customers/models/customer';
import { Dashboard } from '@shared-features/models/dashboard.model';
import { CustomerValidationResult } from '../models/user-validate.model';
import { UserInvoiceType } from '@shared-features/enums/user-invoice-type.enum';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsApiService {
  constructor(private baseAPI: ApiBaseService, private mapper: Mapper) {}

  getOrganizations(
    params: IPagingPayload & ISortPayload & { searchTerm?: string }
  ): Observable<IResponseList<Organization>> {
    return this.baseAPI
      .get(ApiConstant.GET_ALL_ORGANIZATIONS, { params: filterNullEntity(params) })
      .pipe(
        map((res) => ({
          items: this.mapper.fromJson(Organization, res.data.organizations),
          count: res.data.count,
        }))
      );
  }

  getOrganizationById(organizationId: number): Observable<Organization> {
    return this.baseAPI.get(ApiConstant.GET_ORGANIZATION_BY_ID + organizationId).pipe(
      map((res) => {
        return this.mapper.fromJson(Organization, res.data);
      })
    );
  }

  addOrganization(organization: Organization): Observable<Organization> {
    const body = {
      title: organization.name,
      email: organization.email,
      mobileNumber: organization.mobileNumber,
      customerIds: organization.customerIds[0]==="ALL"? organization.customerIds[0]: organization.customerIds,
      pricingMethod: organization.pricingMethod,
      organizationInvoiceType: UserInvoiceType.Both,
    };

    if (organization.contactFirstName) {
      body['contactFirstName'] = organization.contactFirstName;
    }

    if (organization.contactLastName) {
      body['contactLastName'] = organization.contactLastName;
    }

    return this.baseAPI.post(ApiConstant.ADD_ORGANIZATION, body).pipe(
      map((res) => {
        return this.mapper.fromJson(Organization, res.data);
      })
    );
  }

  updateOrganization(
    organization: { organizationId: number; title?: string } & Partial<Organization>
  ): Observable<Organization> {
    return this.baseAPI.put(ApiConstant.UPDATE_ORGANIZATION, organization).pipe(
      map((res) => {
        return this.mapper.fromJson(Organization, res.data);
      })
    );
  }

  addOrganizationCustomers(organization: {
    organizationId: number;
    customerIds: number[];
  }): Observable<Organization> {
    return this.baseAPI.put(ApiConstant.ADD_ORGANIZATION_CUSTOMERS, organization).pipe(
      map((res) => {
        return this.mapper.fromJson(Organization, res.data);
      })
    );
  }

  removeOrganization(id: number): Observable<string> {
    return this.baseAPI.delete(ApiConstant.REMOVE_ORGANIZATION_BY_ID + id);
  }

  removeOrganizationCustomer(organizationId: number, customerId: number): Observable<Organization> {
    return this.baseAPI
      .put(ApiConstant.REMOVE_ORGANIZATION_CUSTOMERS, { organizationId, customerId })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(Organization, res.data);
        })
      );
  }

  getCustomersWithoutOrganization(): Observable<Customer[]> {
    return this.baseAPI.get(ApiConstant.GET_CUSTOMERS_WITHOUT_ORGANIZATION).pipe(
      map((res) => {
        return this.mapper.fromJson(Customer, res.data);
      })
    );
  }

  setTempPassword(body: { id: number; tempPassword: string }) {
    return this.baseAPI.post(ApiConstant.SET_TEMP_PASSWORD, body).pipe(
      map((res) => {
        return res;
      })
    );
  }
  updateOrganizationDynamicDashboard(
    organizationId: number,
    dashboards: Dashboard[]
  ): Observable<Dashboard[]> {
    const payload = { organizationId, dashboards };

    return this.baseAPI.put(ApiConstant.UPDATE_ORGANIZATION_DYNAMIC_BOARD, payload).pipe(
      map((res) => {
        return this.mapper.fromJson(Dashboard, res.data);
      })
    );
  }
  getOrganizationDashboardConfigurations(organizationId: number): Observable<Dashboard[]> {
    return this.baseAPI
      .get(`${ApiConstant.GET_ORGANIZATION_DASHBOARD_CONFIGURATIONS}/${organizationId}`)
      .pipe(
        map((res) => {
          return this.mapper.fromJson(Dashboard, res?.data?.dashboards);
        })
      );
  }

  checkCustomerValidationStatus(body: {
    customerIds: number[];
    callProvider: boolean;
  }): Observable<CustomerValidationResult> {
    return this.baseAPI.post(ApiConstant.CHECK_YCC_USERS_VALIDATE, body).pipe(
      map((res) => {
        return this.mapper.fromJson(CustomerValidationResult, res?.data);
      })
    );
  }
}
